import { useMemo } from 'react';
import { Option } from '@common-fe/common-fe';
import { Enrollment, EnrollmentStatus } from '@common-fe/common-fe';
import dayjs from 'dayjs';
import _ from 'lodash';

import { DEFAULT_DATE_FORMAT } from '@/common/constants.ts';
import { PolicyStatus } from '@/modules/employee/EnrollmentDetails/queries/useGetPolicyStatuses.query';

import { ACTIVE_STATUS_KEY, LimitedUses, PlanTypes } from '../enrollmentModal.types';

import { CurrentPlanInterface } from './useGetEnrollmentSetupData';
import { CurrentHealthPlanInterface } from './useGetHealthPlanEnrollmentSetupData';

interface UseDefaultEnrollmentFormValuesProps {
  partnerName?: string;
  employerName?: string;
  EEID?: string | null;
  PEID?: string | null;
  SSN?: string;
  lastName?: string;
  planType?: string;
  isCurrentPlanInPlans: boolean;
  isEdit?: boolean;
  enrollment?: Enrollment;
  selectedPlanId?: string;
  healthPlanId?: string;
  selectedHealthPlanId?: string;
  currentPlan?: CurrentPlanInterface;
  statuses?: PolicyStatus[];
  currentHealthPlan?: CurrentHealthPlanInterface;
  coverageLevel?: string;
  plansOptions?: Option[];
  healthPlansOptions?: Option[];
}

export function useDefaultEnrollmentFormValues(props: UseDefaultEnrollmentFormValuesProps) {
  const {
    partnerName,
    employerName,
    EEID,
    PEID,
    SSN,
    lastName,
    planType,
    isCurrentPlanInPlans,
    isEdit,
    enrollment,
    selectedPlanId,
    healthPlanId,
    selectedHealthPlanId,
    currentPlan,
    statuses,
    currentHealthPlan,
    coverageLevel,
    plansOptions,
    healthPlansOptions,
  } = props;

  const defaultPlanType =
    planType || (!plansOptions?.length && healthPlansOptions?.length ? PlanTypes.HEALTH : PlanTypes.SAVING);

  return useMemo(
    () => ({
      tpaPartner: partnerName,
      employer: employerName,
      employerEmployeeId: EEID || '',
      partnerEmployeeId: PEID || '',
      ssn: SSN ? `***-**-${SSN}` : '',
      lastName,
      type: defaultPlanType,
      plan: isCurrentPlanInPlans && isEdit ? enrollment?.planId : selectedPlanId,
      healthPlan: isEdit ? healthPlanId : selectedHealthPlanId,
      planYear: currentPlan?.planYear || '',
      planStartDate: currentPlan?.planStartDate || '',
      planEndDate: currentPlan?.planEndDate || '',
      enrollmentStatus:
        isEdit &&
        (enrollment?.enrollmentStatus === EnrollmentStatus.Active ||
          enrollment?.enrollmentStatus === EnrollmentStatus.Canceled)
          ? enrollment?.enrollmentStatus?.charAt(0) || ACTIVE_STATUS_KEY
          : ACTIVE_STATUS_KEY,
      electionAmount: `${_.isNumber(enrollment?.election) ? enrollment?.election : ''}`,
      perPayEmployer: `${_.isNumber(enrollment?.perPayEmployer) ? enrollment.perPayEmployer : ''}`,
      perPayEmployee: `${_.isNumber(enrollment?.perPayEmployee) ? String(enrollment?.perPayEmployee) : ''}`,
      limitedUse: LimitedUses.GENERALE_PURPOSE,
      coverageLevel: enrollment?.coverageTierType,
      coverageLevelEffectiveDate: dayjs(enrollment?.coverageTierEffectiveDate).format(DEFAULT_DATE_FORMAT),
      deductibleMetDate: '',
      enrollmentDate: statuses?.[0]?.validFrom
        ? dayjs(statuses[0].validFrom).format(DEFAULT_DATE_FORMAT)
        : '',

      healthPlanYear: currentHealthPlan?.planYear,
      healthPlanStartDate: currentHealthPlan?.planStartDate,
      healthPlanEndDate: currentHealthPlan?.planEndDate,
      healthPlanEnrollmentStatus: ACTIVE_STATUS_KEY,
      healthPlanCoverageLevel: coverageLevel,
      healthPlanEnrollmentDate: statuses?.[0]?.validFrom
        ? dayjs(statuses[0].validFrom).format(DEFAULT_DATE_FORMAT)
        : isEdit
          ? dayjs().format(DEFAULT_DATE_FORMAT)
          : '',
    }),
    [
      partnerName,
      employerName,
      EEID,
      PEID,
      SSN,
      lastName,
      defaultPlanType,
      isCurrentPlanInPlans,
      isEdit,
      enrollment?.planId,
      enrollment?.enrollmentStatus,
      enrollment?.election,
      enrollment?.perPayEmployer,
      enrollment?.perPayEmployee,
      enrollment?.coverageTierType,
      enrollment?.coverageTierEffectiveDate,
      selectedPlanId,
      healthPlanId,
      selectedHealthPlanId,
      currentPlan?.planYear,
      currentPlan?.planStartDate,
      currentPlan?.planEndDate,
      statuses,
      currentHealthPlan?.planYear,
      currentHealthPlan?.planStartDate,
      currentHealthPlan?.planEndDate,
      coverageLevel,
    ]
  );
}
